import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { addMinutes, differenceInMilliseconds, isBefore } from 'date-fns';
import { jsPDF } from "jspdf";
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GalleryTool } from 'src/app/classes/gallery/gallery.tools';
import { groupRisksByImage, RiskImage } from 'src/app/classes/risks/risk-group';
import { Pop } from 'src/app/classes/shared/pop';
import { Tools } from 'src/app/classes/shared/tools';
import { RiskDetailPanelComponent } from 'src/app/components/project-pages/risks/risk-detail-panel/risk-detail-panel.component';
import { ActionDialogComponent } from 'src/app/components/project-pages/actions/dialogs/action-dialog/action-dialog.component';
import { LinkActionDialogComponent } from 'src/app/components/project-pages/actions/dialogs/link-action-dialog/link-action-dialog.component';
import { RiskReportComponent } from 'src/app/components/project-pages/risks/risk-report/risk-report.component';
import { ALL_ROLES, GalleryComponent, TagDisplayConfig } from 'src/app/components/shared/gallery/gallery.component';
import { Action } from 'src/app/interfaces/api/action';
import { Attachment } from 'src/app/interfaces/api/attachment';
import { Metric, PutMetric } from 'src/app/interfaces/api/metric';
import { Project } from 'src/app/interfaces/api/project';
import { User } from 'src/app/interfaces/api/user';
import { Config } from 'src/app/interfaces/shared/config';
import { ExportProgress } from 'src/app/interfaces/shared/export-progress';
import { TimeFormatPipe } from 'src/app/pipes/time-format.pipe';
import { ActionService } from 'src/app/services/api/actions/action.service';
import { AttachmentService } from 'src/app/services/api/attachments/attachment.service';
import { MetricService } from 'src/app/services/api/metrics/metric.service';
import { ButtonComponent, ChipComponent, SpinnerComponent } from 'ui-library';
import { PDFTool } from '../../../../classes/shared/pdf.tool';

@Component({
  selector: 'app-list-mode',
  imports: [
    TranslateModule,
    CommonModule,
    MatPaginatorModule,
    MatCardModule,
    SpinnerComponent,
    GalleryComponent,
    RiskDetailPanelComponent
  ],
  templateUrl: './list-mode.component.html',
  styleUrls: ['./list-mode.component.scss']
})
export class ListModeComponent implements OnInit, OnChanges, OnDestroy {
  @Input() project!: Project;
  @Input() user!: User;
  @Input() modelId!: string;
  @Input() startDate: string = "";
  @Input() endDate!: string;
  @Input() risksObs!: Observable<any[]>;
  @Input() riskFiltersObs!: Observable<any>;
  @Input() mode: string = "list";
  @Output() onExport: EventEmitter<ExportProgress> = new EventEmitter();

  loading: boolean = false;
  helpText: string = "Les actions suggérées ici sont remplies par un expert chaque jour. Elles sont susceptibles d'être améliorées par vos retours terrains.";
  currentRisk: any;
  destroy$ = new Subject<void>();
  groundOverlay: any;
  pdf = new jsPDF('l', 'px', 'a4');
  colors = Config.colors;
  icons: any;
  risks: any[] = [];
  riskImages: RiskImage[] = [];
  paginatedImages: RiskImage[] = [];
  selectedByImage: Record<string, number> = {};
  numbersByImage: Record<string, Record<string, number>> = {};
  hiddenRiskTitles: string[] = [];
  currentPage: number = 0;
  pageSize: number = 10;
  riskCount: number = 0;
  imageCount: number = 0;
  attachments: any[] = [];
  actionsByRisk: Record<string, { id: string; title: string }[]> = {};
  tagNames: any = {
    risk: [],
    crane: [],
    zone: [],
    action: [],
    worker: []
  }
  tagDisplayConfig: TagDisplayConfig = {
    visibility: {
      risk: { ai: ['dev'], manual: ALL_ROLES }
    }
  };

  constructor(
    private metricsService: MetricService,
    private dialog: MatDialog,
    private attachmentsService: AttachmentService,
    public tools: Tools,
    private pop: Pop,
    private translate: TranslateService,
    private galleryTools: GalleryTool,
    private pdfTool: PDFTool,
    private actionService: ActionService) { }

  async ngOnInit() {
    this.risksObs.subscribe(risks => {
      this.risks = risks;
      this.risks.forEach(risk => risk.payload["displayTitle"] = this.galleryTools.getDisplayName(risk.payload["title"]));
      if (!this.risks.length)
        this.riskCount = 0;
      this.risks.sort((a, b) => isBefore(a.createdDate, b.createdDate) ? 1 : -1);
      this.currentPage = 0;
      this.updatePaginatedRisks();

      this.getAttachments();
    });

    this.riskFiltersObs.subscribe((riskFilters: any) => {
      if (!this.risks.length) return;
      setTimeout(() => {
        let { selectedUnits, sequenceList, zoneList, riskList } = riskFilters;
        // Risk types unchecked in the filter → their boxes are hidden in the gallery
        this.hiddenRiskTitles = (riskList || []).filter((l: any) => !l.value).map((l: any) => l.key);
        this.risks.forEach(m => {
          if (selectedUnits.length && !selectedUnits.includes(m.unit)) {
            return this.setRiskVisibility(m.id, false)
          }
          let risk = riskList.find((l: any) => l.key == m.payload.title);
          if (risk && !risk.value) {
            return this.setRiskVisibility(m.id, false)
          }
          let sequence = sequenceList.find((l: any) => l.key == m.payload.sequence);
          if (sequence && !sequence.value) {
            return this.setRiskVisibility(m.id, false)
          }
          let zone = zoneList.find((l: any) => l.key == m.payload.zone);
          if (zone && !zone.value) {
            return this.setRiskVisibility(m.id, false)
          }
          return this.setRiskVisibility(m.id, true)

        })
        this.currentPage = 0;
        this.updatePaginatedRisks();
      }, 500);
    });

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["project"]?.currentValue && this.tagNames["risk"].length == 0) {
      this.attachmentsService.getTags().pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
        this.parseAllTagNames(res);
      });
    }
    if (changes["project"]?.currentValue) {
      this.loadActionsByRisk();
    }
  }

  private loadActionsByRisk() {
    if (!this.project?.id) return;
    this.actionService.getActions({ project: this.project.id }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (actions: Action[]) => {
        const map: Record<string, { id: string; title: string }[]> = {};
        (actions || []).forEach(a => {
          const ids = ((a.risks as any[]) || []).map(r =>
            r && typeof r === 'object' && 'id' in r ? r.id : r
          );
          const legacyId = !ids.length && (a as any).risk
            ? (typeof (a as any).risk === 'object' ? (a as any).risk.id : (a as any).risk)
            : null;
          const targets = ids.length ? ids : (legacyId ? [legacyId] : []);
          targets.forEach((rid: string) => {
            if (!rid) return;
            (map[rid] ||= []).push({ id: a.id, title: a.title });
          });
        });
        this.actionsByRisk = map;
      },
      error: () => { this.actionsByRisk = {}; },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  parseAllTagNames(tags: any) {
    this.parseTagNames("crane", tags["crane"]);
    this.parseTagNames("risk", tags["risk"]);
    ["worker", "zone", "action"].forEach(project => {
      if (!tags[project]) return;
      this.tagNames[project] = tags[project].map((t: any) => t.name || t.tagName);
      this.tagNames[project].sort((a: string, b: string) => a.localeCompare(b));
    });

    if (tags["action"])
      if (this.project && this.project.enableTags && this.project.enableTags.length !== 0) {
        this.tagNames.action = this.tagNames.action.filter((tagName: string) => {
          if (this.project && this.project.enableTags) {
            const enableTag = this.project.enableTags.find(enableTag => enableTag.name === tagName);
            return enableTag ? enableTag.enabled : true;
          }
          return false;
        });
      }
  }

  parseTagNames(project: string, res: any[] = []) {
    const availableTags = res;
    if (availableTags.length) {
      this.tagNames[project] = availableTags.map(t => t.name || t.tagName).concat(this.project.predictionTags);
      this.tagNames[project].sort((a: string, b: string) => a.localeCompare(b));
      if (this.project.enableTags && this.project.enableTags.length !== 0) {
        this.tagNames[project] = this.tagNames[project].filter((tagName: string) => {
          if (this.project && this.project.enableTags) {
            const enableTag = this.project.enableTags.find(enableTag => enableTag.name === tagName);
            return enableTag ? enableTag.enabled : true;
          }
          return false;
        });
      }
    }
  }

  updatePaginatedRisks() {
    const visible = this.risks.filter(m => m.visible);
    this.riskCount = visible.length;

    const attachmentsById: Record<string, any> = {};
    (this.attachments || []).forEach(a => { if (a) attachmentsById[a.id] = a; });

    this.riskImages = groupRisksByImage(visible, attachmentsById);
    this.imageCount = this.riskImages.length;

    // Default each image's selected tab to its first risk (RG-03) + build the
    // tagId -> number map the gallery uses to badge its risk boxes.
    this.riskImages.forEach(img => {
      if (!this.selectedByImage[img.attachmentId]) {
        this.selectedByImage[img.attachmentId] = img.risks[0]?.number ?? 1;
      }
      // If the image has at least one duplicated risk title, number ALL its boxes
      // (so identical risks can be told apart); otherwise show no numbers.
      const titleCount: Record<string, number> = {};
      img.risks.forEach(r => {
        const title = r.metric.payload.title || '';
        titleCount[title] = (titleCount[title] || 0) + 1;
      });
      const hasDuplicate = Object.values(titleCount).some(c => c > 1);
      const numbers: Record<string, number> = {};
      if (hasDuplicate) {
        img.risks.forEach(r => {
          if (r.box?._id) numbers[r.box._id] = r.number;
        });
      }
      this.numbersByImage[img.attachmentId] = numbers;
    });

    this.updatePaginatedImages();
  }

  updatePaginatedImages() {
    const startIndex = this.currentPage * this.pageSize;
    this.paginatedImages = this.riskImages.slice(startIndex, startIndex + this.pageSize);
  }

  selectedRisk(img: RiskImage): Metric {
    const n = this.selectedByImage[img.attachmentId] ?? 1;
    return (img.risks.find(r => r.number === n) ?? img.risks[0]!).metric;
  }

  onPageChange(event: PageEvent) {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePaginatedImages();
  }

  onCommentEdit(risk: PutMetric) {
    this.metricsService.setMetric(risk).subscribe(res => {
    })
  }

  onShowImage(risk: any) {
    if (risk.gallery)
      this.openGallery(risk, risk.gallery.id);
    else if (risk.payload.attachment) {
      this.openGallery(risk, risk.payload.attachment);
    } else {
      this.attachmentsService.getAttachments({ createdDate: risk.createdDate }).subscribe(attachments => {

        if (attachments && attachments[0]) {
          if (!risk.payload.attachment)
            risk.payload.attachment = attachments[0].id;
          this.openGallery(risk, attachments[0].id);
        } else {
          this.pop.toast(this.translate.instant("risks.image_not_found"), "danger", 4000, "search_off");
        }
      })
    }
  }

  linkAction(risk: Metric) {
    const ref = this.dialog.open(LinkActionDialogComponent, {
      width: '640px',
      data: {
        riskId: risk.id,
        riskTitle: risk.payload['displayTitle'] || risk.payload.title,
        projectId: this.project?.id,
      },
    });
    ref.afterClosed().subscribe(res => {
      if (res) this.loadActionsByRisk();
    });
  }

  openAction(actionId: string) {
    this.actionService.getAction(actionId).pipe(takeUntil(this.destroy$)).subscribe(action => {
      if (!action) return;
      const ref = this.dialog.open(ActionDialogComponent, {
        width: '720px',
        data: { mode: 'edit', action: { ...action }, projectId: this.project?.id },
      });
      ref.afterClosed().subscribe(res => { if (res) this.loadActionsByRisk(); });
    });
  }

  reportRisk(risk: Metric) {
    const dialogRef = this.dialog.open(RiskReportComponent, {
      width: "600px",
      data: {
        risk: risk
      }
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res?.ok && res.comment) {
        risk.payload.comment = res.comment;
        risk.payload["status"] = res.status;
        this.metricsService.reportRisk(risk).subscribe(result => {
          this.pop.toast(this.translate.instant("risks.report_save"), "success", 4000, "save");
        })
      }
    })
  }

  async openGallery(risk: Metric, attachmentId: string) {
    let index = -1;

    const getImageInfo = (dir: string) => {
      return new Promise((resolve, reject) => {
        let risks = this.risks.filter(m => m.visible);

        if (dir == "next") {
          index--;
        } else if (dir == "prev") {
          index++;
        }

        if (dir == "") {
          index = risks.findIndex(m => m.id == risk?.id);
        }

        if (index <= -1)
          index = 0;

        if (index > risks.length - 1)
          index = risks.length - 1;

        attachmentId = risks[index].payload.attachment;
        this.currentRisk = risks[index];
        let attachmentIndex = this.attachments.findIndex(a => a.id == attachmentId);

        if (attachmentIndex >= 0) {
          resolve(this.getImageInfoData(this.attachments[attachmentIndex], getImageInfo));
        } else
          this.attachmentsService.getAttachment(attachmentId).subscribe(attachment => {
            if (attachment) {
              this.attachments.push(attachment);
              this.currentRisk.gallery = attachment;
              resolve(this.getImageInfoData(attachment, getImageInfo))
            } else {
              this.pop.toast(this.translate.instant("risks.image_not_found"), "danger", 4000, "search_off");
              reject("no image found");
            }
          });
      })
    }

    const dialogRef = this.dialog.open(GalleryComponent, {
      width: "auto",
      height: "auto",
      data: await getImageInfo("")
    });

    dialogRef.afterClosed().subscribe((res) => {
    });

  }

  getImageInfoData(attach: Attachment, getImageInfo: Function) {
    return {
      index: 0,
      id: attach.id,
      name: attach.name,
      tracker: attach.tracker,
      project: this.project,
      urls: [attach.url],
      tags: attach.tags,
      startDate: attach.createdDate,
      endDate: addMinutes(attach.createdDate, 1).toISOString(), // ...
      processed: attach.processed,
      user: this.user,
      customVisionIds: attach.customVisionIds ?? {},
      type: "Attachments",
      boxes: attach.predictions,
      onUpdate: new EventEmitter(),
      tagDisplayConfig: this.tagDisplayConfig,
      getImageInfo
    }
  }

  loadIcons() {
    return new Promise((resolve, reject) => {

      try {
        var icons = ['danger', 'hauteur', 'plain-pied', 'effondrement', 'levage', 'coactivite', 'ensevelissement', 'posture'];

        var loadCount = 0;
        if (!this.icons) {
          this.icons = {};
          icons.forEach((icon) => {

            let xhr, url;
            url = `/models/risks/SVG/${icon}.svg`;

            xhr = new XMLHttpRequest();
            xhr.open('GET', url, true);
            xhr.onload = async () => {
              this.icons[icon] = xhr.responseText;
              loadCount++;
              if (loadCount == icons.length) resolve(true)
            }
            xhr.send(null);
          })

        } else {
          resolve(true)
        }

      } catch (e) {
        console.error(e)
      }
    })
  }

  loadImageAsPNG(url: string, height: number, width: number) {
    return new Promise((resolve, reject) => {
      let sourceImage = new Image();

      sourceImage.onload = () => {
        let png = new Image();
        let cnv = document.createElement('canvas'); // doesn't actually create an element until it's appended to a parent,
        // so will be discarded once this function has done it's job
        cnv.height = height;
        cnv.width = width;

        let ctx = cnv.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(sourceImage, 0, 0, height, width);
        png.src = cnv.toDataURL(); // defaults to image/png
        //resolve(png)
        resolve(png.src);
      }
      // sourceImage.onerror = reject;

      sourceImage.src = url;
    });
  }

  async getAttachments() {
    if (!this.risks.length) return;
    this.loading = true;
    let ids = this.risks.map(m => m.payload.attachment);
    this.attachmentsService.getAttachments({ ids }).pipe(takeUntil(this.destroy$)).subscribe(att => {
      this.attachments = att;
      this.handleRisks(this.risks, att);
      this.updatePaginatedRisks();
      setTimeout(() => {
        this.loading = false;
      }, 500)
    }, err => {
      this.pop.error(`An error occured while fetching images [status ${err.status}]`);
      this.loading = false;
    })
  }

  async exportPDF() {
    const visibleRisks = this.risks.filter(risk => risk.visible && risk.gallery);
    await this.pdfTool.exportRisksToPDF({
      risks: visibleRisks,
      project: this.project,
      startDate: this.startDate,
      endDate: this.endDate,
      onProgress: (current, total) => this.onExport.emit({ loading: true, current, total }),
    });
    this.onExport.emit({ loading: false });
  }

  async getAttachment(attachmentId: string) {
    return new Promise(res => {
      let sub = this.attachmentsService.getAttachment(attachmentId).subscribe(att => {
        sub.unsubscribe();
        res(att);
      })
    })
  }

  handleRisks(risks: Metric[], attachments: Attachment[]) {
    attachments.sort((a, b) => isBefore(a.createdDate, b.createdDate) ? 1 : -1);

    attachments.forEach(a => {
      if (a.tags && a.tags.risk && a.tags.risk.find(p => p.probability == 1)) {
        a.tagged = true;
        a.prediction = a.tags.risk?.filter(p => p.probability == 1).map(p => p.tagName).join(", ");
      }
    })
    let handledRisks = risks.map(metric => {

      if (!metric.payload.startDate || !metric.payload.endDate) return metric;

      var ms = differenceInMilliseconds(metric.payload.endDate, metric.payload.startDate);
      const totalSeconds = Math.floor(ms / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      var s = `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

      metric.payload.duration = s;

      metric.gallery = attachments.find(a => a.id == metric.payload.attachment);
      return metric;
    });

    this.risks = this.risks.concat(handledRisks.filter(m => this.risks.findIndex(met => met.id == m.id) == -1));
  }

  getUnitOrTrackerName(metric: Metric) {
    let unitName = metric.unitName || (metric.unit && this.tools.isHex(metric.unit as string));
    if (unitName) return unitName;
    let trackerName = metric.trackerName || metric.trackerName || (metric.tracker && this.tools.isHex(metric.tracker as string));
    if (trackerName) return trackerName;
    return metric.unit || metric.tracker || "No Ressource";
  }

  setRiskVisibility(metricId: string, visible: boolean) {
    let risk = this.risks.find(m => m.id == metricId);
    if (risk)
      risk.visible = visible;
  }
}
